import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PWAInstallContext = createContext(null);

const DISMISS_KEY = 'pwa-install-banner-dismissed';
const DISMISS_TTL = 24 * 60 * 60 * 1000; // 24 hours

const isIOS =
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const isAndroid = /Android/i.test(navigator.userAgent);

const isMobileDevice =
  isAndroid ||
  isIOS ||
  /webOS|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(navigator.userAgent);

const isInStandaloneMode =
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

function isDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_TTL;
  } catch {
    return false;
  }
}

export function PWAInstallProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(
    () => window.deferredInstallPrompt || null
  );
  const [dismissed, setDismissed] = useState(() => isDismissedRecently());
  const [installed, setInstalled] = useState(isInStandaloneMode);

  // Whether the banner should be visible at all
  const shouldShowBanner =
    isMobileDevice && !isInStandaloneMode && !installed && !dismissed;

  useEffect(() => {
    if (!isMobileDevice || isInStandaloneMode) return;

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      window.deferredInstallPrompt = e;
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      window.deferredInstallPrompt = null;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Pick up prompt that was captured before this provider mounted
    if (window.deferredInstallPrompt) {
      setDeferredPrompt(window.deferredInstallPrompt);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // localStorage not available — dismiss only lasts this render
    }
  }, []);

  const triggerInstall = useCallback(async () => {
    if (!deferredPrompt) return false;

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setInstalled(true);
        setDeferredPrompt(null);
        window.deferredInstallPrompt = null;
        return true;
      }
    } catch (err) {
      console.error('PWA install prompt error:', err);
    }
    return false;
  }, [deferredPrompt]);

  return (
    <PWAInstallContext.Provider
      value={{
        isMobileDevice,
        isIOS,
        isAndroid,
        isInStandaloneMode,
        deferredPrompt,
        shouldShowBanner,
        dismiss,
        triggerInstall,
      }}
    >
      {children}
    </PWAInstallContext.Provider>
  );
}

export function usePWAInstall() {
  const ctx = useContext(PWAInstallContext);
  if (ctx === null) {
    throw new Error('usePWAInstall must be used within a PWAInstallProvider');
  }
  return ctx;
}
